import { Accordion, Flex, Menu, Text } from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import { memo, useRef, useState, useEffect } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import {
  useUndoAllMutation,
  useUpsertEntityTypeDetections,
} from 'src/data-capture/detected-entities/hooks/DetectionActionMutations';
import {
  EntityCategory,
  selectCategoryStateCounts,
  selectEntityLabel,
  selectExistingColumnVisible,
  selectValidationErrorsInCategory,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';
import EntityDataPanel from './EntityDataPanel';
import { AfternoonDuplicateFieldApiError } from 'src/error/ApiError';
import { Logger } from 'src/utils/Logger';
import { notifications } from '@mantine/notifications';
import { EntityLayoutProvider, useEntityLayout } from './EntityLayoutProvider';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { ValidationErrorsModal } from './ValidationErrorsModal';

type Props = {
  entityType: string;
  category: EntityCategory;
  isActive: boolean;
};

function EntityTypeAccordionItem({ entityType, category, isActive }: Props) {
  return (
    <EntityLayoutProvider>
      <EntityTypeAccordionItemContent
        entityType={entityType}
        category={category}
        isActive={isActive}
      />
    </EntityLayoutProvider>
  );
}

function EntityTypeAccordionItemContent({ entityType, category, isActive }: Props) {
  const { upsertEntityTypeDetections } = useUpsertEntityTypeDetections(category, entityType);
  const { clientId } = useCurrentClient();
  const discardAll = useDetectedEntitiesStore(state => state.discardAll);
  const entityLabel = useDetectedEntitiesStore(state =>
    selectEntityLabel(state, category, entityType),
  );
  const [opened, setOpened] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const { undoAllInCategoryAndEntityType } = useUndoAllMutation();
  const stateCounts = useDetectedEntitiesStore(state =>
    selectCategoryStateCounts(state, category, entityType),
  );
  const showExisting = useDetectedEntitiesStore(
    state => isActive && selectExistingColumnVisible(state, category, entityType),
  );
  const validationErrors = useDetectedEntitiesStore(state =>
    selectValidationErrorsInCategory(state, category, entityType),
  );
  const titleRef = useRef<HTMLDivElement>(null);
  const { registerTitleWidth, titleWidth } = useEntityLayout();
  const { showDataUploadCounts } = useFlags();

  useEffect(() => {
    if (titleRef.current) {
      registerTitleWidth(titleRef.current.clientWidth);
    }
  }, [entityLabel, stateCounts.total, registerTitleWidth]);

  const handleDiscardAll = () => {
    discardAll(category, entityType);
  };

  const handleMenuClick = (opened: boolean) => {
    setOpened(opened);
  };

  async function handleImportAll() {
    if (validationErrors.length > 0) {
      setShowValidationErrors(true);
      return;
    }

    try {
      await upsertEntityTypeDetections(clientId);
    } catch (error) {
      if (AfternoonDuplicateFieldApiError.isDuplicateFieldError(error)) {
        Logger.error(`Duplicate field error upserting entities for category ${category}`, error, {
          duplicateField: error.field,
        });
        notifications.show({
          color: 'red',
          title: 'Duplicate value',
          message: error.message,
        });
      } else {
        Logger.error(`Failed upserting all entities for category ${category}`, error);
        notifications.show({
          color: 'red',
          title: 'Action failed',
          message: 'Something went wrong, please try again.',
        });
      }
    }
  }

  return (
    <Accordion.Item role="region" value={entityType} key={entityType}>
      <Accordion.Control>
        <Flex>
          <Flex gap="xs" ref={titleRef} mr="md" style={{ minWidth: titleWidth }}>
            <Text fw={700} size="md" c="gray.10" style={{ whiteSpace: 'nowrap' }}>
              {entityLabel}
            </Text>
            {showDataUploadCounts && (
              <Text fw={700} size="md" c="gray.6">
                {stateCounts.total}
              </Text>
            )}
          </Flex>
          <Flex flex={1} pl="md">
            {isActive && (
              <Text fw={700} size="md" c="gray.10">
                New
              </Text>
            )}
          </Flex>
          {showExisting && (
            <Flex flex={1}>
              <Text fw={700} size="md" c="gray.10">
                Existing
              </Text>
            </Flex>
          )}
          <Flex miw={180} justify={'flex-end'} pr={'sm'}>
            <Menu opened={opened} onChange={handleMenuClick}>
              <span onClick={e => e.stopPropagation()}>
                <Menu.Target>
                  <IconDots />
                </Menu.Target>
                <Menu.Dropdown>
                  {stateCounts.active > 0 && (
                    <>
                      <Menu.Item onClick={handleImportAll}>Add all as new</Menu.Item>
                      <Menu.Item onClick={handleDiscardAll}>Discard all</Menu.Item>
                    </>
                  )}
                  {stateCounts.resolved + stateCounts.discarded > 0 && (
                    <Menu.Item
                      onClick={() =>
                        undoAllInCategoryAndEntityType(clientId, category, entityType, false)
                      }
                    >
                      Undo all
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </span>
            </Menu>
          </Flex>
        </Flex>
      </Accordion.Control>
      <Accordion.Panel>
        <EntityDataPanel entityType={entityType} category={category} />
      </Accordion.Panel>
      <ValidationErrorsModal
        opened={showValidationErrors}
        close={() => setShowValidationErrors(false)}
        validationErrors={validationErrors}
      />
    </Accordion.Item>
  );
}

export default memo(EntityTypeAccordionItem);
